import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import TsHome from "@/pages/TsHome";
import TsPortionGate from "@/components/mobile/TsPortionGate";
import TsAppShell from "@/components/mobile/TsAppShell";
import { teamPathFromLegacyTs } from "@/lib/teamHub";

const DriverHandoff = lazy(() => import("@/pages/DriverHandoff"));
const ProductionHub = lazy(() => import("@/pages/ProductionHub"));
const InventoryHub = lazy(() => import("@/pages/InventoryHub"));
const CaHub = lazy(() => import("@/pages/CaHub"));
const TcHub = lazy(() => import("@/pages/TcHub"));
const WeHub = lazy(() => import("@/pages/WeHub"));
const SaHub = lazy(() => import("@/pages/SaHub"));
const DesignGalleryHub = lazy(() => import("@/pages/DesignGalleryHub"));
const DriverPartnerJoin = lazy(() => import("@/pages/DriverPartnerJoin"));

function Fallback() {
  return (
    <div className="flex items-center justify-center py-20 min-h-[100dvh]" style={{ background: "#F5F2EA" }}>
      <div className="spinner" />
    </div>
  );
}

function LegacyTsRedirect() {
  const { pathname, search } = useLocation();
  return <Navigate to={`${teamPathFromLegacyTs(pathname)}${search}`} replace />;
}

/**
 * team.thetabletop.in route tree — `/` is login-or-home; apps have clean paths.
 * Legacy `/ts/...` redirects here so bookmarks keep working while we retire the prefix.
 */
export default function TeamHubRoutes() {
  return (
    <Routes>
      <Route path="/" element={<TsHome />} />
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/ts" element={<Navigate to="/" replace />} />
      <Route path="/ts/" element={<Navigate to="/" replace />} />
      <Route path="/ts/login" element={<Navigate to="/" replace />} />
      <Route path="/ts/*" element={<LegacyTsRedirect />} />
      <Route path="/tb" element={<Navigate to="/" replace />} />
      <Route path="/tb/*" element={<Navigate to="/" replace />} />

      <Route
        path="/kitchen"
        element={
          <TsAppShell title="Kitchen" portionKind="kitchen">
            <TsPortionGate portion="kitchen">
              <Suspense fallback={<Fallback />}>
                <ProductionHub />
              </Suspense>
            </TsPortionGate>
          </TsAppShell>
        }
      />
      <Route
        path="/delivery"
        element={
          <TsAppShell title="Delivery" portionKind="driver">
            {/* Every other tile is gated here. Delivery leaned on the driver
                bootstrap token alone, so its route stayed reachable after the
                tile was withdrawn. */}
            <TsPortionGate portion="driver">
              <Suspense fallback={<Fallback />}>
                <DriverHandoff />
              </Suspense>
            </TsPortionGate>
          </TsAppShell>
        }
      />
      <Route
        path="/inventory"
        element={
          <TsAppShell title="Inventory" portionKind="inventory">
            <TsPortionGate portion="inventory">
              <Suspense fallback={<Fallback />}>
                <InventoryHub />
              </Suspense>
            </TsPortionGate>
          </TsAppShell>
        }
      />
      <Route
        path="/reports"
        element={
          <TsAppShell hideBar portionKind="chat">
            <TsPortionGate portion="chat">
              <Suspense fallback={<Fallback />}>
                <CaHub />
              </Suspense>
            </TsPortionGate>
          </TsAppShell>
        }
      />
      <Route
        path="/salary"
        element={
          <TsAppShell hideBar portionKind="salary">
            <TsPortionGate portion="salary">
              <Suspense fallback={<Fallback />}>
                <SaHub />
              </Suspense>
            </TsPortionGate>
          </TsAppShell>
        }
      />
      <Route
        path="/chat"
        element={
          <TsAppShell hideBar portionKind="chat">
            <TsPortionGate portion="chat">
              <Suspense fallback={<Fallback />}>
                <TcHub />
              </Suspense>
            </TsPortionGate>
          </TsAppShell>
        }
      />
      <Route
        path="/work-ethic"
        element={
          <TsAppShell hideBar portionKind="we">
            <TsPortionGate portion="we">
              <Suspense fallback={<Fallback />}>
                <WeHub />
              </Suspense>
            </TsPortionGate>
          </TsAppShell>
        }
      />
      <Route
        path="/designs"
        element={
          <TsAppShell title="Designs" portionKind="gallery">
            <TsPortionGate portion="gallery">
              <Suspense fallback={<Fallback />}>
                <DesignGalleryHub />
              </Suspense>
            </TsPortionGate>
          </TsAppShell>
        }
      />

      {/* Rider onboarding still uses Delivery join on this host */}
      <Route
        path="/dd/join"
        element={
          <Suspense fallback={<Fallback />}>
            <DriverPartnerJoin />
          </Suspense>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
